/**
 * Trustpilot Adapter (Story 23.3) — OPENS Epic 23 Trustpilot slice.
 *
 * SECOND review-platform integration in our adapter library (after Google
 * Reviews 23.1+23.2 closed). SIXTH per-user-supplied API-key family but
 * the FIRST mixing OAuth-2 `client_credentials` grant with per-user-secret
 * scaffold AND requiring proactive-refresh on a per-user-secret family
 * AND requiring TWO secrets in the connect dialog.
 *
 * # FIVE divergences from CallRail 22.1 / Moz 20.7 (the closest analogues):
 *
 * 1. **TWO secrets (API Key + API Secret) in connect dialog** — FIRST
 *    3-input API-key dialog. The HTTP Basic-encoded pair exchanges for a
 *    100h Bearer access token via OAuth-2 `client_credentials` grant.
 *    Per `developers.trustpilot.com/grant-type-client-credentials/`
 *    verified 2026-05-16.
 *
 * 2. **100-hour finite-TTL access token** — `expires_in: 359999` per the
 *    grant. NO refresh token (the docs explicitly say "you can't refresh
 *    access tokens that you've obtained with the Client Credentials grant
 *    type — when your access token expires, you must issue the same
 *    request to get a new token"). FIRST per-user-API-key family with
 *    finite numeric TTL (Ahrefs/Semrush/Moz/CallRail/CTM all permanent).
 *
 * 3. **TWO base URLs in the Trustpilot API surface** — Story 23.3 ONLY
 *    consumes `api.trustpilot.com/v1`. The invitations-api host is
 *    reserved for a future write-mutation story (Round 2 Pre-mortem E).
 *
 * 4. **NO data-retention pre-flight** (DIVERGENT from CallRail 22.1's
 *    25-month ceiling). Trustpilot reviews persist for the lifetime of
 *    the business unit — no retention cap to pre-flight. Verified
 *    `legal.trustpilot.com/end-user-terms-and-conditions` +
 *    `business.trustpilot.com/terms` 2026-05-16. **Future maintainer**:
 *    do NOT add a bogus retention guard by copying CallRail's pattern.
 *
 * 5. **Domain-to-BusinessUnitID discovery flow** — Step 1 of the connect
 *    dialog requires a Trustpilot domain (e.g. `greenlifewellness.com`).
 *    The adapter resolves via `GET /v1/business-units/find?name={domain}`
 *    (API-key auth — the ONLY unscoped Trustpilot endpoint). 2-step
 *    wizard auto-skips Step 2 when find returns exactly 1 BU.
 *
 * # THREE structural concerns:
 *
 * - **Paid-tier gating** — `/v1/private/business-units/{id}/reviews`
 *   requires Trustpilot Plus or higher. Connect-time probe sets
 *   `metadata.privateApiEntitled`; `fetchMetrics` skips the private
 *   endpoint when false (saves 1 request + prevents 403).
 *
 * - **Reviewer PII discipline** — `consumer.displayName`,
 *   `consumer.displayLocation`, `consumer.email` (paid tier), review
 *   `title`/`text`, `companyReply.text`, `referralEmail`/`referenceId`/
 *   `tags` (paid tier). The `sanitizeTrustpilotPii` helper redacts these
 *   before any log/Sentry breadcrumb.
 *
 * - **`source` and `status` enum drift** — Trustpilot does NOT publish
 *   canonical enum values; adapter passes them through as raw strings
 *   AND emits a forensic `console.log` ONCE per fetch on unknown values
 *   (deduped via request-scoped Set). Mirrors Google Reviews 23.1 AC #9
 *   unknown-state handling verbatim.
 *
 * # API_KEY_PLATFORMS divergence
 *
 * `authType: "api-key"` (mirroring Ahrefs/Semrush/Moz/CallRail/CTM/
 * PageSpeed) but **DO NOT** add `"trustpilot"` to
 * `API_KEY_PLATFORMS` in `adapter.ts`. That Set is reserved for
 * env-binding API-key platforms (currently `["google-pagespeed"]`);
 * Trustpilot is per-user-secret like the other 5 non-PageSpeed API-key
 * families.
 *
 * # GreenLife Wellness pre-wired posture
 *
 * Story 23.3 does NOT touch `demo-accounts.ts`. GreenLife already has
 * `"trustpilot"` in `platforms[]` (line 714) AND `"trustpilot":
 * "demo-1700005"` in `accountIds` (line 719). DIVERGENT from Moz 20.8 +
 * CTM 22.4 + Google Reviews 23.1 (extended at adapter-ship time); SAME
 * as CallRail 22.2 + Google Reviews 23.2 (pre-wired posture).
 *
 * @see _bmad-output/implementation-artifacts/23-3-trustpilot-adapter.md
 * @see _bmad/bmm/data/integration-metrics/trustpilot.md
 */
import type {
  AdapterContext,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  EndpointDiscoveryInfo,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RateLimitConfig,
  AccountInfo,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";

import {
  fetchTrustpilotBusinessUnitDetails,
  fetchTrustpilotInvitationData,
  fetchTrustpilotPaginatedReviews,
  fetchTrustpilotPrivateReviews,
  fetchTrustpilotPublicReviews,
  mergeTrustpilotPublicAndPrivateReviews,
  TRUSTPILOT_API_BASE,
  TRUSTPILOT_INVITATIONS_API_BASE,
  TRUSTPILOT_MAX_REVIEW_PAGES,
} from "./api";
import {
  TRUSTPILOT_DIMENSIONS,
  TRUSTPILOT_METRICS,
} from "./metric-allowlist";
import { validateTrustpilotInvitationsDateRange } from "./retention";
// Round 2 fix R2-11 — single source of truth for review-id coercion.
import { coerceTrustpilotReviewId } from "./safe-fetch";
import {
  buildTrustpilotReviewDistribution,
  buildTrustpilotReviewSummary,
  transformTrustpilotBusinessUnitDetails,
  transformTrustpilotInvitationData,
  transformTrustpilotReview,
} from "./transformers";

const PLATFORM = "trustpilot" as const;
const QUERY_TIMEOUT_MS = 8_000;

/** Story 23.3 — single-page review fetch (max 100). Pagination deferred. */
const TRUSTPILOT_REVIEWS_PAGE_SIZE = 100;

/** Re-export the API base + version so downstream consumers can build URLs. */
export { TRUSTPILOT_API_BASE, TRUSTPILOT_INVITATIONS_API_BASE };
export const TRUSTPILOT_API_VERSION = API_VERSIONS.trustpilot;

/** Tuple literal for adapter registration parity. */
export const TRUSTPILOT_PLATFORM_VALUES = ["trustpilot"] as const;
export type TrustpilotPlatform = (typeof TRUSTPILOT_PLATFORM_VALUES)[number];

const RATE_LIMITS: RateLimitConfig = {
  requestsPerMinute: 150, // 833/5min = ~166/min raw; declare 150 conservative
  burstLimit: 5,
  // Hourly enforced by queue throttling layer — Trustpilot docs: 10K/hr.
};

/**
 * Read trusted Trustpilot metadata from the per-integration KV side-channel.
 * The adapter expects the route handler to have populated this at connect
 * time. Falls back to the `ctx.accountId` for `businessUnitId` (parity).
 *
 * **Cross-check invariant (Round 3 Red Team Attack #5 — BU-ID enumeration
 * defense)**: the submitted `businessUnitId` MUST appear in
 * `metadata.allowedBusinessUnitIds[]`. The route handler enforces this at
 * connect time; the adapter re-enforces it at fetchMetrics time as
 * defense-in-depth.
 */
interface TrustpilotMetadata {
  readonly businessUnitId: string;
  readonly displayName: string;
  readonly country?: string;
  readonly privateApiEntitled: boolean;
  readonly allowedBusinessUnitIds: ReadonlyArray<string>;
  readonly domain: string;
}

/**
 * Read the metadata from KV at `trustpilot:meta:{integrationId}`. Returns
 * null when missing (the adapter degrades to ctx.accountId — but the
 * cross-check still fires if `allowedBusinessUnitIds` ends up empty).
 */
async function readTrustpilotMetadata(
  ctx: AdapterContext
): Promise<TrustpilotMetadata | null> {
  const kv = ctx.env.KV as
    | {
        get(key: string, opts?: { type?: "json" }): Promise<unknown>;
      }
    | undefined;
  if (!kv || typeof kv.get !== "function") return null;
  try {
    const raw = await kv.get(`trustpilot:meta:${ctx.integrationId}`, {
      type: "json",
    });
    if (!raw || typeof raw !== "object") return null;
    const obj = raw as Record<string, unknown>;
    if (
      typeof obj.businessUnitId !== "string" ||
      typeof obj.privateApiEntitled !== "boolean" ||
      !Array.isArray(obj.allowedBusinessUnitIds)
    ) {
      return null;
    }
    return {
      businessUnitId: obj.businessUnitId,
      displayName: typeof obj.displayName === "string" ? obj.displayName : "",
      country: typeof obj.country === "string" ? obj.country : undefined,
      privateApiEntitled: obj.privateApiEntitled,
      allowedBusinessUnitIds: (obj.allowedBusinessUnitIds as unknown[]).filter(
        (v): v is string => typeof v === "string"
      ),
      domain: typeof obj.domain === "string" ? obj.domain : "",
    };
  } catch {
    return null;
  }
}

/**
 * Forensic logger for unknown `source` and `status` enum values per AC #7.
 * Deduped via a request-scoped Set keyed on `${field}:${value}` so a
 * single fetch with 50 reviews carrying the same unknown source logs
 * exactly ONCE.
 */
function logUnknownEnumOnce(
  seen: Set<string>,
  ctx: AdapterContext,
  field: "source" | "status",
  value: string
): void {
  const dedupeKey = `${field}:${value}`;
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);
  console.log(
    JSON.stringify({
      event: `trustpilot_unknown_${field}`,
      value,
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
    })
  );
}

const KNOWN_TRUSTPILOT_SOURCES: ReadonlySet<string> = new Set([
  "Organic",
  "InvitationLink",
  "Api",
]);
const KNOWN_TRUSTPILOT_STATUSES: ReadonlySet<string> = new Set([
  "active",
  "reported",
]);

// ============================================================================
// Story 23.4 — Fixture-mode helper (loadTrustpilotFixture)
// ============================================================================

/**
 * Story 23.4 Task 5 — fixture-mode loader. Invoked from the very first
 * statement of `fetchMetrics` when `ctx.fixtures === true`. Mirrors the
 * GR 23.2 + CTM 22.4 + CallRail 22.2 patterns with FOUR divergences:
 *
 *   1. GreenLife `platforms[]` pre-wired by Story 23.3 — SAME as GR 23.2's
 *      pre-wired posture. (DIVERGENT from CTM 22.4 + Moz 20.8 + GR 23.1
 *      which extended at adapter-ship time.)
 *   2. NO retention pre-flight — Trustpilot has no retention ceiling per
 *      Story 23.3 AC #4 (SAME as GR 23.2 + CTM 22.4 + Moz 20.8;
 *      DIVERGENT from CallRail 22.2's 25-month bypass).
 *   3. Aggregate rows SKIP date-filter; per-review rows DO date-filter —
 *     Trustpilot's `/v1/business-units/{id}/reviews` endpoint has no
 *     server-side date filter so the production adapter at
 *     trustpilot.ts:363-369 applies a client-side post-filter to
 *     per-review rows ONLY. Aggregate rows reflect lifetime statistics
 *     regardless of `dateRange`. Round 2 Pre-mortem D invariant.
 *   4. Force-stamps `metadata.privateApiEntitled: true` — Story 23.4 NEW
 *      invariant; surfaces full Plus-tier feature set for demos including
 *      build-to-sell preset #7 (content-integrity-flag pie). Round 4 ADR-D.
 *
 * Structured-log telemetry (AC #18) — exactly ONE of these 4 events fires:
 *   - `trustpilot_demo_fixture_loaded` (success)
 *   - `trustpilot_demo_fixture_missing` (KV miss)
 *   - `trustpilot_demo_fixture_binding_missing` (KV or DB missing)
 *   - `trustpilot_demo_fixture_resolver_failed` (getClientSlug throw)
 *
 * Round 3 Red Team #1 invariant: fixture mode MUST NOT touch the
 * `trustpilot:credentials:*` / `trustpilot:meta:*` / `trustpilot:probe:*`
 * KV namespaces — the helper only reads `fixture:trustpilot:{slug}` via
 * the FDL `getFixtureFromKV` helper (which scopes via the SQL-layer
 * `getClientSlug` tenant-scoped lookup).
 */
async function loadTrustpilotFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "KV",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Trustpilot demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_binding_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        missingBinding: "DB",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message:
        "Missing DB binding — cannot resolve Trustpilot demo client slug",
      retryable: false,
    });
  }

  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    // Round 3 review fix — surface invalid-context as a 5th forensic
    // event so operators can diagnose tenant-scope regressions with the
    // same `trustpilot_demo_fixture_*` namespace as the 4 documented
    // failure modes. Logs the missing field name only, not its value.
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_invalid_context",
        integrationId: ctx.integrationId,
        missingTenantId:
          typeof ctx.tenantId !== "string" || ctx.tenantId.length === 0,
        missingClientId:
          typeof ctx.clientId !== "string" || ctx.clientId.length === 0,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message:
        "Trustpilot demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  const { getFixtureFromKV } = await import("../fixtures/kv-loader");
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_resolver_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        errorClass: err instanceof Error ? err.constructor.name : typeof err,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Trustpilot demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "trustpilot",
    clientSlug,
  );

  if (metrics === null) {
    const kvKey = `fixture:trustpilot:${clientSlug}`;
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_missing",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Trustpilot demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Shape validation — defense against KV poisoning / schema drift
  // (matches GR 23.2 Pass 1 Blind Hunter C2 + Pass 3 Blind Hunter M1).
  const isValidShape =
    typeof metrics === "object" &&
    metrics !== null &&
    metrics.platform === "trustpilot" &&
    Array.isArray(metrics.records) &&
    metrics.records.every((r) => r !== null && typeof r === "object");
  if (!isValidShape) {
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_corrupt",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        kvKey: `fixture:trustpilot:${clientSlug}`,
        cachedPlatform: metrics.platform,
        recordsType: Array.isArray(metrics.records) ? "array" : typeof metrics.records,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "invalid_response",
      message: `Trustpilot demo fixture for client ${clientSlug} is corrupt (wrong platform or non-array records); re-run pnpm fixtures:generate + pnpm fixtures:upload`,
      retryable: false,
    });
  }

  // Round 2 review fix — inverted-dateRange validation is now lifted to
  // the top of `fetchMetrics` so it covers both production and fixture
  // paths (no duplicate guard here).

  // Round 2 Pre-mortem D — apply client-side date filter to per-review rows
  // ONLY (matches production parity at trustpilot.ts:363-369). Aggregate
  // rows (businessUnitDetails / reviewSummary / reviewDistribution /
  // contentIntegrityFlag) reflect lifetime statistics regardless of dateRange.
  //
  // Round 2 review note on aggregate-vs-per-review semantics: the fixture
  // generator builds `reviewSummary` and `reviewDistribution` from the
  // FULL 80-review wire-format set at generate time. In production the
  // adapter recomputes the same aggregates AFTER date-filtering, so
  // `reviewSummary.reviewsInPeriod` IS the period count. In fixture-mode
  // these aggregates are baked in as lifetime totals and the
  // `dataIsPartial` flag (derived below) tells consumers whether the
  // per-review rows match the aggregate counts. Widgets that bind to
  // `reviewSummary.reviewsInPeriod` should treat `dataIsPartial === true`
  // as "summary is lifetime; list is windowed" — surface a "Showing N of
  // M reviews" hint per AC #16 graceful-degradation pattern.
  //
  // Round 1 review fix — per-review rows with malformed/missing createdAt
  // are DROPPED (not kept), to avoid time-series widgets bucketing on NaN
  // x-values or table-sort pinning. Aggregate rows continue to pass through.
  //
  // Round 2 review fix — track dropped-undated count separately so the
  // success log can surface a breadcrumb when undated rows were silently
  // removed (helps operators distinguish "date filter trimmed the set"
  // from "schema drift dropped corrupt rows").
  const startMs = dateRange.start.getTime();
  const endMs = dateRange.end.getTime();
  let undatedDroppedCount = 0;
  const filteredRecords = metrics.records.filter((r) => {
    const rec = r as { _reportType?: string; createdAt?: unknown };
    if (rec._reportType !== "trustpilotReview") return true; // aggregate row
    if (typeof rec.createdAt !== "string") {
      undatedDroppedCount++;
      return false;
    }
    const t = Date.parse(rec.createdAt);
    if (!Number.isFinite(t)) {
      undatedDroppedCount++;
      return false;
    }
    return t >= startMs && t <= endMs;
  });
  if (undatedDroppedCount > 0) {
    console.log(
      JSON.stringify({
        event: "trustpilot_demo_fixture_undated_rows_dropped",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        clientSlug,
        droppedCount: undatedDroppedCount,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  // Derive forensic counts from the actual per-review records that remain
  // after date-filtering — `totalReviewCount` reflects the full underlying
  // fixture (lifetime), `fetchedReviewCount` reflects the filtered set.
  const cachedMeta = (metrics.metadata ?? {}) as Record<string, unknown>;
  const totalReviewCount = metrics.records.filter(
    (r) => (r as { _reportType?: string })._reportType === "trustpilotReview",
  ).length;
  const fetchedReviewCount = filteredRecords.filter(
    (r) => (r as { _reportType?: string })._reportType === "trustpilotReview",
  ).length;

  const completedAt = new Date();
  // Round 1 review fix — derive `dataIsPartial` honestly from whether the
  // date filter excluded any per-review rows. Force-stamping `false` lied
  // when a tight dateRange shrank the per-review set (e.g., dashboard
  // shows 80 lifetime reviews in scorecard but 7 rows in the list).
  const dataIsPartial = fetchedReviewCount < totalReviewCount;

  // Round 2 review fix — multi-tenant safety. Source `businessUnitId` and
  // `domain` from the cached fixture metadata when present, falling back
  // to the GreenLife canonical values only when metadata is absent. This
  // prevents a future second Trustpilot demo client from inheriting
  // GreenLife's BU id / domain via the loader.
  const resolvedBusinessUnitId =
    typeof cachedMeta.businessUnitId === "string" && cachedMeta.businessUnitId.length > 0
      ? cachedMeta.businessUnitId
      : metrics.accountId || "demo-1700005";
  const resolvedDomain =
    typeof cachedMeta.domain === "string" && cachedMeta.domain.length > 0
      ? cachedMeta.domain
      : "greenlifewellness.com";

  // Round 2 review fix — `fetchedAt` from the cached generator's
  // `metrics.fetchedAt` (deterministic — generator uses `config.endDate`)
  // rather than `new Date()` (which makes every fixture call return a
  // different value, defeating snapshot-equality assertions and
  // determinism guarantees). `completedAt` is still used for the
  // operator-facing `durationMs` telemetry below.
  const fetchedAt =
    metrics.fetchedAt instanceof Date && Number.isFinite(metrics.fetchedAt.getTime())
      ? metrics.fetchedAt
      : completedAt;

  const result: AdapterMetrics = {
    ...metrics,
    accountId: ctx.accountId || metrics.accountId || resolvedBusinessUnitId,
    dateRange,
    records: filteredRecords,
    metadata: {
      ...cachedMeta,
      // Force-stamped invariants per AC #8 Step 9
      dataFreshnessLagHours: 0,
      dataIsPartial,
      totalReviewCount,
      fetchedReviewCount,
      // Round 4 ADR-D — fixture forces private-tier elevation
      privateApiEntitled: true,
      businessUnitId: resolvedBusinessUnitId,
      domain: resolvedDomain,
    },
    recordCount: filteredRecords.length,
    fetchedAt,
  };

  console.log(
    JSON.stringify({
      event: "trustpilot_demo_fixture_loaded",
      integrationId: ctx.integrationId,
      tenantId: ctx.tenantId,
      clientSlug,
      recordCount: result.recordCount,
      businessUnitId: resolvedBusinessUnitId,
      privateApiEntitled: true,
      // Round 3 review fix — surface the undated-drops count in the
      // single success-log line so operators don't have to cross-correlate
      // two events to understand "why did recordCount fall from 91 to 88".
      undatedDroppedCount,
      durationMs: completedAt.getTime() - startedAt,
      timestamp: completedAt.toISOString(),
    }),
  );

  return adapterOk(result);
}

/**
 * Validate (and log) `source` / `status` enum values per AC #7.
 * Returns the raw value unchanged — Trustpilot doesn't publish canonical
 * lists, so we pass-through and forensically log on unknowns.
 */
function checkEnumValue(
  seen: Set<string>,
  ctx: AdapterContext,
  field: "source" | "status",
  value: unknown,
  known: ReadonlySet<string>
): void {
  if (typeof value !== "string" || value.length === 0) return;
  if (!known.has(value)) {
    logUnknownEnumOnce(seen, ctx, field, value);
  }
}

// ============================================================================
// Adapter implementation
// ============================================================================

export const trustpilotAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",
  rateLimits: RATE_LIMITS,

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 23.4 Round 2 review fix — validate dateRange BEFORE both the
    // fixture short-circuit and the production path. Inverted ranges
    // silently dropped all results in Story 23.3's production path
    // (trustpilot.ts:363-369 keep-if-NaN behavior); guard at top.
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_request",
        message: `Trustpilot fetchMetrics: dateRange.start (${dateRange.start.toISOString()}) must be on or before dateRange.end (${dateRange.end.toISOString()})`,
        retryable: false,
      });
    }

    // Story 23.4 — fixture-mode short-circuit. MUST run BEFORE the
    // credentials.accessToken read, BEFORE businessUnitId validation, and
    // BEFORE any upstream fetches. Mirrors GR 23.2 + CTM 22.4 + CallRail
    // 22.2 short-circuit patterns. Drift-log fires when `ctx.fixtures` is
    // truthy-but-not-strictly-`true` (defensive — operator-facing).
    const rawFixtures = (ctx as unknown as { fixtures?: unknown }).fixtures;
    if (
      rawFixtures !== undefined &&
      rawFixtures !== true &&
      rawFixtures !== false
    ) {
      console.log(
        JSON.stringify({
          event: "trustpilot_fixtures_flag_unexpected_type",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          actualType: typeof rawFixtures,
          timestamp: new Date().toISOString(),
        }),
      );
    }
    const fixturesEnabled = rawFixtures === true;
    if (fixturesEnabled) {
      return loadTrustpilotFixture(ctx, dateRange);
    }

    const accessToken = credentials.accessToken;

    // Read the KV side-channel metadata. The API Key lives in the
    // `trustpilot:credentials:{id}` KV (encrypted-at-rest); the adapter
    // doesn't read it directly because the route handler has already
    // decrypted both into `credentials.accessToken` (Bearer) for this call.
    const metadata = await readTrustpilotMetadata(ctx);
    if (!metadata) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Trustpilot integration metadata is missing — the route handler did not populate `trustpilot:meta:{integrationId}`.",
        retryable: false,
      });
    }

    const businessUnitId = ctx.accountId ?? metadata.businessUnitId;

    // Round 3 Red Team Attack #5 — BU-ID enumeration defense. Reject any
    // businessUnitId that wasn't in the discovered allowlist at connect
    // time. The route handler also enforces this, but defense-in-depth
    // catches future code paths that bypass the route.
    if (
      metadata.allowedBusinessUnitIds.length > 0 &&
      !metadata.allowedBusinessUnitIds.includes(businessUnitId)
    ) {
      return adapterErr({
        code: "account_not_found",
        message: `Trustpilot business unit "${businessUnitId}" is not in the discovered allowlist for this integration.`,
        retryable: false,
      });
    }

    const fetchedAt = new Date();
    const unknownEnumsSeen = new Set<string>();
    const records: Record<string, unknown>[] = [];

    // === Endpoint Group A — Business Unit Details ===========================
    // Use Bearer auth uniformly for the cron-driven sync path. Trustpilot's
    // public BU endpoint accepts EITHER apikey or Bearer; using Bearer
    // means the adapter only needs the one secret in `credentials`. The
    // raw API Key + Secret live in the `trustpilot:credentials:{id}` KV
    // side-channel and are only read by the cron recoveryRefresh handler.
    const buDetailsResult = await fetchTrustpilotBusinessUnitDetails(
      businessUnitId,
      { accessToken },
      { decryptedAccessToken: accessToken }
    );
    if (!buDetailsResult.success) return adapterErr(buDetailsResult.error);
    const buDetailsRow = transformTrustpilotBusinessUnitDetails(
      buDetailsResult.data,
      businessUnitId
    );
    records.push(buDetailsRow);

    // === Endpoint Group B — Public Service Reviews (page 1) =================
    const publicReviewsResult = await fetchTrustpilotPublicReviews(
      businessUnitId,
      { accessToken },
      { page: 1, perPage: TRUSTPILOT_REVIEWS_PAGE_SIZE },
      { decryptedAccessToken: accessToken }
    );
    if (!publicReviewsResult.success) return adapterErr(publicReviewsResult.error);
    const publicReviews = publicReviewsResult.data.reviews;

    // === Story 35.3 Batch 3 — Paginated history beyond page 1 ===============
    //
    // Per AC #7, 3 entry-conditions trigger pagination. Default (no opt-in,
    // default 30-day range) = page 1 only — preserves Story 23.3 quota
    // footprint per Round 2 Pre-mortem A.
    //
    // The 3 entry-conditions (any one OR'd in is sufficient):
    //   1. ctx.preferences.lifetimeMode === true (explicit caller opt-in)
    //   2. ctx.preferences.maxReviewPages > 1 (explicit caller opt-in)
    //   3. dateRange.start < pageOneOldestCreatedAt (implicit opt-in —
    //      the requested date range extends beyond what page 1 covers)
    //
    // The hard cap of `TRUSTPILOT_MAX_REVIEW_PAGES` (6) protects the
    // Cloudflare Workers subrequest budget regardless of caller.
    const rawPrefs =
      (ctx as unknown as { preferences?: Record<string, unknown> }).preferences ??
      {};
    const lifetimeMode = rawPrefs.lifetimeMode === true;
    const requestedMaxPages =
      typeof rawPrefs.maxReviewPages === "number" &&
      Number.isFinite(rawPrefs.maxReviewPages) &&
      rawPrefs.maxReviewPages > 0
        ? Math.min(
            Math.floor(rawPrefs.maxReviewPages),
            TRUSTPILOT_MAX_REVIEW_PAGES
          )
        : 1;

    // Compute page-1-oldest createdAt for the implicit opt-in condition.
    let pageOneOldestMs = Number.POSITIVE_INFINITY;
    for (const r of publicReviews) {
      const c = typeof r.createdAt === "string" ? Date.parse(r.createdAt) : NaN;
      if (Number.isFinite(c) && c < pageOneOldestMs) pageOneOldestMs = c;
    }
    const implicitTrigger =
      Number.isFinite(pageOneOldestMs) &&
      dateRange.start.getTime() < pageOneOldestMs;

    // Code-review round 1 fix #21 — when `lifetimeMode === true` OR explicit
    // `maxReviewPages > 1`, populate lifetime aggregates EVEN when page 1 is
    // short (the BU has < 100 lifetime reviews and no pagination needed).
    // Without this, a fully-fetched 87-review BU with `lifetimeMode=true`
    // would render "—" for all 4 lifetime metrics.
    const callerExplicitlyOptedIn = lifetimeMode || requestedMaxPages > 1;
    const pageOneFull = publicReviews.length === TRUSTPILOT_REVIEWS_PAGE_SIZE;
    const paginationTriggered =
      (callerExplicitlyOptedIn || implicitTrigger) && pageOneFull;

    let lifetimeReviews: ReadonlyArray<Record<string, unknown>> | null = null;
    if (paginationTriggered) {
      // Code-review round 1 fix #2 — simplify the dead conditional. When
      // lifetimeMode is true, use the hard cap; otherwise honor explicit
      // request or implicit-trigger default (page-2 only, clamped to 6).
      const effectiveMaxPages = lifetimeMode
        ? TRUSTPILOT_MAX_REVIEW_PAGES
        : Math.min(
            Math.max(requestedMaxPages, implicitTrigger ? 2 : 1),
            TRUSTPILOT_MAX_REVIEW_PAGES
          );
      const additionalPages = effectiveMaxPages - 1;
      // Code-review round 1 fix #3 — defense: if no additional pages needed,
      // do not invoke the paginated helper (the helper's Math.max(1, ...)
      // clamp would otherwise fetch a useless page-2 request, wasting
      // Trustpilot's per-host budget).
      if (additionalPages >= 1) {
        const paginatedResult = await fetchTrustpilotPaginatedReviews(
          businessUnitId,
          { accessToken },
          {
            startPage: 2,
            maxPages: additionalPages,
            perPage: TRUSTPILOT_REVIEWS_PAGE_SIZE,
          },
          { decryptedAccessToken: accessToken }
        );
        if (paginatedResult.success) {
          // Merge page-1 + paginated set (dedupe defensively).
          const mergedLifetime = [...publicReviews];
          // Code-review round 1 fix #23 — coerce id to string for dedup so
          // numeric IDs (Trustpilot shape drift) still get deduped.
          const seenIds = new Set<string>(
            publicReviews
              .map((r) => coerceTrustpilotReviewId(r.id))
              .filter((id): id is string => id !== null)
          );
          for (const r of paginatedResult.data.reviews) {
            const id = coerceTrustpilotReviewId(r.id);
            if (id && seenIds.has(id)) continue;
            if (id) seenIds.add(id);
            mergedLifetime.push(r);
          }
          lifetimeReviews = mergedLifetime;
          console.log(
            JSON.stringify({
              event: "trustpilot_paginated_history_triggered",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              maxPages: effectiveMaxPages,
              requestsConsumed: 1 + paginatedResult.data.pageCount,
              totalReviewsFetched: mergedLifetime.length,
              capped: paginatedResult.data.capped,
              timestamp: new Date().toISOString(),
            })
          );
          // Code-review round 1 fix #17 — surface partial-failure mid-stream
          // (helper now returns success-with-partial-error instead of a hard
          // adapterErr, preserving the page-1+pages-fetched-so-far data).
          if (paginatedResult.data.partialError) {
            console.log(
              JSON.stringify({
                event: "trustpilot_paginated_history_partial",
                integrationId: ctx.integrationId,
                tenantId: ctx.tenantId,
                errorCode: paginatedResult.data.partialError.code,
                failedAtPage: paginatedResult.data.partialError.failedAtPage,
                pagesSucceeded: paginatedResult.data.pageCount,
                reviewsRecovered: paginatedResult.data.reviews.length,
                timestamp: new Date().toISOString(),
              })
            );
          }
        }
        // Note: paginatedResult.success === false is now unreachable since
        // fetchTrustpilotPaginatedReviews ALWAYS returns success post round 1
        // fix #17 — but we keep the defensive shape for forward-compat.
      }
    } else if (callerExplicitlyOptedIn && publicReviews.length > 0) {
      // Code-review round 1 fix #21 — caller explicitly opted into lifetime
      // metrics but page 1 was short (BU has < 100 lifetime reviews). No
      // pagination needed; use page-1 as the lifetime set so widgets bind
      // honest values instead of "—".
      lifetimeReviews = publicReviews;
    }

    // === Endpoint Group C — Private Service Reviews (paid tier only) ========
    let privateReviews: ReadonlyArray<Record<string, unknown>> | null = null;
    if (metadata.privateApiEntitled) {
      const privateResult = await fetchTrustpilotPrivateReviews(
        businessUnitId,
        accessToken,
        { page: 1, perPage: TRUSTPILOT_REVIEWS_PAGE_SIZE },
        { decryptedAccessToken: accessToken }
      );
      if (privateResult.success) {
        privateReviews = privateResult.data.reviews;
      } else if (privateResult.error.providerStatus === 403) {
        // The customer's entitlement was downgraded mid-flight. Don't
        // abort — degrade to public-only (saves data continuity) and
        // log forensically so the integration card UI can surface
        // "private fields no longer available" on the next sync.
        console.log(
          JSON.stringify({
            event: "trustpilot_private_tier_403_downgrade",
            integrationId: ctx.integrationId,
            tenantId: ctx.tenantId,
          })
        );
      } else {
        return adapterErr(privateResult.error);
      }
    }

    const mergedReviews = mergeTrustpilotPublicAndPrivateReviews(
      publicReviews,
      privateReviews
    );

    // === Date-range post-filter (client-side — Trustpilot has no server-side
    // date filter) ============================================================
    // Story 23.4 Round 2 review fix — DROP per-review rows with missing or
    // malformed `createdAt` (previously kept). Production parity with
    // `loadTrustpilotFixture` filter behavior. Undated rows would pollute
    // time-series widgets (NaN x-axis) and table sort ordering.
    //
    // Round 3 review fix — emit forensic log when undated rows are dropped,
    // matching the fixture-mode telemetry pattern. Operators triaging an
    // unexpected review-count drop in production deserve the same
    // diagnostic signal as fixture-mode operators.
    let productionUndatedDroppedCount = 0;
    const filteredReviews = mergedReviews.filter((r) => {
      const createdAtStr = typeof r.createdAt === "string" ? r.createdAt : null;
      if (!createdAtStr) {
        productionUndatedDroppedCount++;
        return false; // drop undated rows
      }
      const t = Date.parse(createdAtStr);
      if (!Number.isFinite(t)) {
        productionUndatedDroppedCount++;
        return false; // drop unparseable dates
      }
      return t >= dateRange.start.getTime() && t <= dateRange.end.getTime();
    });
    if (productionUndatedDroppedCount > 0) {
      console.log(
        JSON.stringify({
          event: "trustpilot_production_undated_rows_dropped",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          droppedCount: productionUndatedDroppedCount,
          timestamp: new Date().toISOString(),
        })
      );
    }

    // === Aggregations (reviewSummary + reviewDistribution) =================
    // Story 23.4 — pass `buDetailsResult.data` (raw business-unit details)
    // to the summary helper so it can compute the 3 synthetic-aggregate
    // metrics (`reviewVelocityPerMonth`, `reputationTrendDirection`,
    // `trustScoreEligibleReviewPercentage`) referenced by the
    // reputation-scorecard preset #8 (ADR-C platform-specific 4th KPI).
    // Round 2 fix R2-4 — REVERTED round 1 fix #16 merge of in-period
    // privateReviews into the lifetime set. The two sets have DIFFERENT
    // date scopes (lifetimeReviews = full paginated public history;
    // privateReviews = in-period only). Merging produces metrics that
    // are neither lifetime nor in-period — silently corrupts aggregates.
    //
    // The proper fix would be to ALSO paginate the private endpoint to
    // get full-lifetime private rows, but that doubles the subrequest
    // budget (12 pages vs 6) and Trustpilot's /v1/private/business-units/
    // {id}/reviews has the same per-page cost as the public path. For
    // Story 35.3 scope: lifetime aggregates are PUBLIC-only by design,
    // documented in the metric description below. A future Story 35.3.3
    // can add paginated-private lifetime aggregates if customer feedback
    // demands them.
    records.push(
      buildTrustpilotReviewSummary(
        filteredReviews,
        businessUnitId,
        buDetailsResult.data,
        // Story 35.3 Batch 3 — pass lifetime records when pagination
        // triggered. NULL when default-page-1-only behavior — Story 23.3
        // baseline; summary emits 4 lifetime aggregates as NULL.
        lifetimeReviews
      )
    );
    for (const row of buildTrustpilotReviewDistribution(
      filteredReviews,
      businessUnitId
    )) {
      records.push(row);
    }

    // === Per-review records =================================================
    let verifiedReviewCount = 0;
    let companyReplyCount = 0;
    for (const r of filteredReviews) {
      const row = transformTrustpilotReview(r, {
        businessUnitId,
        privateApiEntitled: metadata.privateApiEntitled,
      });
      records.push(row);
      if (row.isVerified === true) verifiedReviewCount++;
      if (row.hasReply === true) companyReplyCount++;
      // Forensic enum-drift logging (AC #7)
      if (metadata.privateApiEntitled) {
        checkEnumValue(unknownEnumsSeen, ctx, "source", row.source, KNOWN_TRUSTPILOT_SOURCES);
        checkEnumValue(unknownEnumsSeen, ctx, "status", row.status, KNOWN_TRUSTPILOT_STATUSES);
      }
    }

    // === Story 35.3 Batch 2 — Invitations API (Endpoint Group D) ============
    //
    // Plus-tier-gated via the existing `privateApiEntitled` flag (NO new
    // entitlement probe per Story 35.3 AC #3). Pre-flight via the
    // 30-day retention helper per AC #4 — skip silently if range is
    // entirely outside retention; clamp `dateRange.start` if partial.
    if (metadata.privateApiEntitled) {
      const preflight = validateTrustpilotInvitationsDateRange(
        dateRange,
        new Date()
      );
      if (preflight.skip) {
        // Range entirely outside the 30-day window OR entirely future
        // (Round 2 fix R2-1) — silently skip, emit forensic log so
        // operators can diagnose "why no invitations data on this report?"
        console.log(
          JSON.stringify({
            event:
              preflight.reason === "future_only"
                ? "trustpilot_invitations_skipped_future_only"
                : "trustpilot_invitations_skipped_outside_retention",
            integrationId: ctx.integrationId,
            tenantId: ctx.tenantId,
            dateRangeStart: dateRange.start.toISOString(),
            dateRangeEnd: dateRange.end.toISOString(),
            retentionCutoff: preflight.retentionCutoff.toISOString(),
            reason: preflight.reason,
            timestamp: new Date().toISOString(),
          })
        );
      } else {
        // Determine clamped start for Invitations call ONLY (do NOT
        // touch Service Reviews / BU Details — they have NO retention
        // cap per Story 23.3 AC #4 carry-forward).
        const invitationsStart =
          preflight.clamped?.start ?? dateRange.start;
        const invitationsEnd = preflight.clamped?.end ?? dateRange.end;

        if (preflight.clamped) {
          // Round 2 fix R2-6 — surface the distinct `reason` so operators
          // can attribute the clamp to retention vs future-end.
          console.log(
            JSON.stringify({
              event: "trustpilot_invitations_date_range_clamped",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              reason: preflight.reason,
              originalStart: dateRange.start.toISOString(),
              originalEnd: dateRange.end.toISOString(),
              clampedStart: invitationsStart.toISOString(),
              clampedEnd: invitationsEnd.toISOString(),
              retentionCutoff: preflight.retentionCutoff.toISOString(),
              timestamp: new Date().toISOString(),
            })
          );
        }

        const invitationsResult = await fetchTrustpilotInvitationData(
          businessUnitId,
          accessToken,
          invitationsStart,
          invitationsEnd,
          { decryptedAccessToken: accessToken }
        );
        if (invitationsResult.success) {
          // Code-review round 1 fix #31 — emit partial-shape-drift log
          // when some but not all documented keys are present. Helper
          // attached `__trustpilot_invitations_partial_drift__` as a
          // non-enumerable property on the response object.
          const partialDrift = (
            invitationsResult.data as Record<string, unknown> & {
              __trustpilot_invitations_partial_drift__?: {
                expectedKeys: ReadonlyArray<string>;
                actualKeys: ReadonlyArray<string>;
              };
            }
          ).__trustpilot_invitations_partial_drift__;
          if (partialDrift) {
            console.log(
              JSON.stringify({
                event: "trustpilot_invitations_shape_drift",
                integrationId: ctx.integrationId,
                tenantId: ctx.tenantId,
                kind: "partial_keys_missing",
                expectedKeys: partialDrift.expectedKeys,
                actualKeys: partialDrift.actualKeys,
                timestamp: new Date().toISOString(),
              })
            );
          }
          records.push(
            transformTrustpilotInvitationData(
              invitationsResult.data,
              businessUnitId
            )
          );
        } else if (invitationsResult.error.providerStatus === 403) {
          // Customer downgraded mid-flight — degrade silently per AC #21
          // (mirrors Story 23.3 Endpoint Group C 403-fallback pattern).
          // Do NOT cascade — the in-period aggregates already shipped.
          console.log(
            JSON.stringify({
              event: "trustpilot_invitations_403_downgrade",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              timestamp: new Date().toISOString(),
            })
          );
        } else if (invitationsResult.error.code === "invalid_response") {
          // Full shape drift — emit `trustpilot_invitations_shape_drift`
          // per AC #6 + #21 and degrade silently. Do NOT throw.
          console.log(
            JSON.stringify({
              event: "trustpilot_invitations_shape_drift",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              providerMessage: invitationsResult.error.providerMessage,
              timestamp: new Date().toISOString(),
            })
          );
        } else {
          // Any other error — log + degrade (do NOT cascade). The
          // forensic log lets operators triage Trustpilot outages.
          console.log(
            JSON.stringify({
              event: "trustpilot_invitations_fetch_failed",
              integrationId: ctx.integrationId,
              tenantId: ctx.tenantId,
              errorCode: invitationsResult.error.code,
              timestamp: new Date().toISOString(),
            })
          );
        }
      }
    } else {
      // No entitlement — log ONCE per fetch (deduped via single emission).
      console.log(
        JSON.stringify({
          event: "trustpilot_skipped_invitations_no_entitlement",
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          timestamp: new Date().toISOString(),
        })
      );
    }

    // === Structured completion log per AC #18 ===============================
    const unknownSourceValues = Array.from(unknownEnumsSeen).filter((v) =>
      v.startsWith("source:")
    ).length;
    const unknownStatusValues = Array.from(unknownEnumsSeen).filter((v) =>
      v.startsWith("status:")
    ).length;
    const trustScore =
      typeof buDetailsRow.trustScore === "number" ? buDetailsRow.trustScore : 0;
    const stars =
      typeof buDetailsRow.stars === "number" ? buDetailsRow.stars : 0;
    const totalReviewCount =
      typeof buDetailsRow.totalReviews === "number" ? buDetailsRow.totalReviews : 0;
    console.log(
      JSON.stringify({
        event: "trustpilot_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        businessUnitId,
        domain: metadata.domain,
        privateApiEntitled: metadata.privateApiEntitled,
        reviewsFetched: filteredReviews.length,
        totalReviewCount,
        trustScore,
        stars,
        verifiedReviewCount,
        companyReplyCount,
        unknownSourceValues,
        unknownStatusValues,
        recordCount: records.length,
        timestamp: fetchedAt.toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId: businessUnitId,
      dateRange,
      records,
      fetchedAt,
      recordCount: records.length,
      metadata: {
        dataFreshnessLagHours: 0,
        privateApiEntitled: metadata.privateApiEntitled,
      },
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Cheapest possible probe — single GET to /business-units/{id} (~200 bytes
    // response). Same shape as the cron's healthCheck handler per AC #6.
    const metadata = await readTrustpilotMetadata(ctx);
    const businessUnitId = ctx.accountId ?? metadata?.businessUnitId;
    if (!businessUnitId) {
      return adapterErr({
        code: "invalid_request",
        message:
          "Trustpilot validateConnection requires a businessUnitId — none on ctx or metadata.",
        retryable: false,
      });
    }
    const result = await fetchTrustpilotBusinessUnitDetails(
      businessUnitId,
      { accessToken: credentials.accessToken },
      { decryptedAccessToken: credentials.accessToken }
    );
    if (!result.success) return adapterErr(result.error);
    return adapterOk(true);
  },

  async discoverAccounts(
    ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Pass 2 fix — Trustpilot's `/v1/business-units/find` endpoint requires
    // an API KEY in the `apikey:` HTTP header (NOT a Bearer access token).
    // The adapter only receives `credentials.accessToken` (the OAuth-2
    // Bearer) at fetchMetrics/discoverAccounts time; the raw API Key is
    // held in the `trustpilot:credentials:{id}` KV side-channel but
    // decryption requires `decryptToken(value, tenantId, userId,
    // {ENCRYPTION_KEY})` which is outside the adapter's surface.
    //
    // For re-discovery, return the connect-time snapshot from
    // `metadata.allowedBusinessUnitIds`. This satisfies AC #21's
    // "re-authorization flows where the customer needs to switch business
    // units within the same Trustpilot organization without re-pasting
    // credentials" — the BU set is fixed per-domain, so the snapshot is
    // authoritative. To force a fresh probe (e.g., if Trustpilot adds a
    // new BU under the same domain), the customer re-runs the
    // /trustpilot/probe route which DOES have access to the decrypted
    // API Key.
    const metadata = await readTrustpilotMetadata(ctx);
    if (!metadata || metadata.allowedBusinessUnitIds.length === 0) {
      return adapterErr({
        code: "account_not_found",
        message:
          "Your Trustpilot business unit is no longer accessible — re-connect via the Integration Picker to refresh the discovered Business Units.",
        retryable: false,
      });
    }
    const accounts: AccountInfo[] = metadata.allowedBusinessUnitIds.map(
      (id) => ({
        id,
        name: id === metadata.businessUnitId ? metadata.displayName : id,
        platform: PLATFORM,
        metadata: {
          country: id === metadata.businessUnitId ? metadata.country : undefined,
          domain: metadata.domain,
        },
      })
    );
    return adapterOk(accounts);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return [...TRUSTPILOT_METRICS];
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return [...TRUSTPILOT_DIMENSIONS];
  },

  async discoverEndpoints(
    ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    // Story 23.4 Round 3 review fix — fixture mode force-stamps
    // `privateApiEntitled: true` in `fetchMetrics` (Round 4 ADR-D), but
    // `discoverEndpoints` previously read only from KV metadata. The
    // mismatch meant a fixture-mode demo would expose private fields in
    // records yet the widget-edit-panel gating would still show "Upgrade
    // to Trustpilot Plus" on preset #7. Detect fixture mode here and
    // mirror the elevation so both surfaces agree.
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: unknown }).fixtures === true;
    const metadata = await readTrustpilotMetadata(ctx);
    const privateAvailable =
      fixturesEnabled || metadata?.privateApiEntitled === true;
    return adapterOk({
      endpoints: [
        { key: "businessUnitDetails", status: "available" },
        { key: "publicReviews", status: "available" },
        {
          key: "privateReviews",
          status: privateAvailable ? "available" : "subscription_required",
          subscriptionRequired: privateAvailable
            ? undefined
            : "Trustpilot Plus tier or higher",
        },
        {
          key: "productReviews",
          status: "subscription_required",
          subscriptionRequired:
            "Reserved for future story (Product Reviews API requires per-SKU mapping)",
        },
        {
          // Story 35.3 — Invitations API READ surface now ships. The
          // WRITE-mutation surface (POST /v1/private/business-units/{id}/
          // email-invitations + reply-to-review + tag-write endpoints)
          // remains reserved for a future Story 35.3.1.
          //
          // Code-review round 1 fix #13 — wording aligned with Story 23.3's
          // `privateReviews` entry above ("Trustpilot Plus tier or higher").
          // The `privateApiEntitled` flag is shared across the two paid-
          // tier surfaces (private reviews + invitations); same gating
          // semantic → same advertised tier requirement.
          key: "invitations",
          status: privateAvailable ? "available" : "subscription_required",
          subscriptionRequired: privateAvailable
            ? undefined
            : "Trustpilot Plus tier or higher",
        },
        {
          key: "dataSolutions",
          status: "subscription_required",
          subscriptionRequired:
            "Trustpilot Enterprise bulk export — reserved for future Enterprise-tier story",
        },
      ],
    });
  },
};

// Acknowledge intentionally-unused param (typescript noUnusedParameters)
void QUERY_TIMEOUT_MS;
